import React, {useEffect, useState} from 'react';
import {LazyLoadImage} from 'react-lazy-load-image-component';

// Import Custom Component
import ProductNav from '../product-nav';
import ALink from '../../../common/ALink';
import ProductTabs from "../tabs/product-tabs";
import {removeXSSAttacks} from "../../../../utils";
import useTranslation from "../../../../utils/hooks/useTranslation";
import usePrice from "../../../../utils/hooks/usePrice";
import {connect} from "react-redux";
import {showLoginModal} from "../../../../store/modal/actions";
import {addProductToCart, saveCart} from "../../../../store/cart/actions";

// Normalises a product's attribute options into a consistent grouped shape:
//   [{ id, name, options: [{ id, value }] }]
// so the detail view can render them regardless of how the API exposes them.
function getProductAttributes(product) {
    // Preferred: grouped attributes, mirroring the category attribute shape
    //   attributes: [{ id, name, options: [{ id, value }] }]
    if (Array.isArray(product?.attributes) && product.attributes.length) {
        return product.attributes
            .map((attr) => ({
                id: attr.id,
                name: attr.name,
                options: (attr.options || []).map((o) => ({id: o.id, value: o.value})),
            }))
            .filter((attr) => attr.options.length);
    }

    // Fallback: a flat list of the product's selected options, each carrying
    // its parent attribute — group them by attribute.
    //   attribute_options: [{ id, value, attribute: { id, name } }]
    if (Array.isArray(product?.attribute_options) && product.attribute_options.length) {
        const groups = {};
        product.attribute_options.forEach((opt) => {
            const attr = opt.attribute || {};
            const key = attr.id ?? attr.name ?? 'other';
            if (!groups[key]) groups[key] = {id: key, name: attr.name || '', options: []};
            groups[key].options.push({id: opt.id, value: opt.value});
        });
        return Object.values(groups);
    }

    return [];
}

function ProductDetails(props) {
    const {t} = useTranslation();
    const {c} = usePrice();
    const {
        product,
        adClass = "col-lg-7 col-md-6",
        prev,
        next,
        isNav = true,
        parent = ".product-single-default",
        isSticky = false,
        showTabs = true,
        user,
        showLoginModal,
        addProductToCart,
        saveCart
    } = props;

    const productAttributes = getProductAttributes(product);

    useEffect(() => {
        if (product) {
            document.querySelector(`${parent} .shopping-cart`) && document.querySelector(`${parent} .shopping-cart`).classList.remove('disabled');
            document.querySelector(`${parent} .sticky-cart .add-cart`) && document.querySelector(`${parent} .sticky-cart .add-cart`).classList.remove('disabled');
        }
    }, []);

    function parseQuantity(quantity) {
        if (quantity > 100) {
            return <span>100+</span>;
        } else if (quantity > 50) {
            return <span>50+</span>;
        }

        return <span>{quantity}</span>
    }

    function onAddCartClick(e) {
        e.preventDefault();

        if (!user) {
            showLoginModal();
            return;
        }

        const cartProduct = {
            id: product.id,
            slug: product.slug,
            quantity: 1,
            type: 'product',
            name: product.name,
            maxQuantity: product.quantity,
            price: product.price,
            images: product.images,
        }

        addProductToCart(cartProduct);
        saveCart();
    }

    return (
        <>
            <div className={`skel-pro skel-detail ${adClass}`}></div>
            {
                product &&
                <div className={`product-single-details ${adClass}`}>
                    <h1 className="product-title">{product.name}</h1>

                    {
                        isNav ?
                            <ProductNav prev={prev} next={next}/>
                            : ""
                    }

                    <hr className="short-divider"/>

                    {product.price > 0 && <div className="price-box">
                        <span className="product-price">{c(product.price)}</span>
                    </div>}

                    {user && product.quantity > 0 && (
                        <div>
                            <p>{t("label.quantity")}: {parseQuantity(product.quantity)}</p>
                        </div>
                    )}

                    <div className="product-desc">
                        <p>{product.short_description}</p>
                        <p dangerouslySetInnerHTML={removeXSSAttacks(product.long_description)}
                           className={"long-description-format"}/>
                    </div>

                    <ul className="single-info-list">
                        {
                            productAttributes.map((attribute) => (
                                <li key={`single-attr-${attribute.id}`} className="product-attribute">
                                    {attribute.name ? <span className="attribute-name">{attribute.name}: </span> : null}
                                    {attribute.options.map((option, index) => (
                                        <React.Fragment key={`single-attr-opt-${option.id}`}>
                                            <strong>{option.value}</strong>
                                            {index < attribute.options.length - 1 ? ', ' : ''}
                                        </React.Fragment>
                                    ))}
                                </li>
                            ))
                        }
                        {
                            !product.tags == null && product.tags.length > 0 ?
                                <li>
                                    {t("label.tags")}: {product.tags.map((item, index) =>
                                    (
                                        <React.Fragment key={`single-cat-${index}`}>
                                            <strong>
                                                <ALink href={{pathname: '/shop', query: {tag: item.slug}}}
                                                       className="category">{item.name}</ALink>
                                            </strong>
                                            {index < product.tags.length - 1 ? ', ' : ''}
                                        </React.Fragment>
                                    ))
                                }
                                </li>
                                : ''
                        }
                    </ul>

                    {
                        isSticky &&
                        <div className="sticky-wrapper">
                            <div className="sticky-header desktop-sticky sticky-cart d-none d-lg-block">
                                <div className="container">
                                    <div className="sticky-img mr-4 media-with-lazy">
                                        <figure className="mb-0">
                                            <LazyLoadImage
                                                src={process.env.REACT_APP_PUBLIC_ASSET_URI + product.images[0].url}
                                                width="100%"
                                                height="auto"
                                                alt={product.name}
                                            />
                                        </figure>
                                    </div>
                                    <div className="sticky-detail">
                                        <div className="sticky-product-name">
                                            <h2 className="product-title w-100 ls-0">{product.name}</h2>
                                        </div>
                                    </div>

                                    <div className="product-action">
                                        {/* eslint-disable-next-line*/}
                                        <a href="#" className={`btn btn-dark add-cart mr-2 `} title={t("label.buy")}
                                           onClick={onAddCartClick}>{t("label.buy")}</a>
                                    </div>
                                </div>
                            </div>
                        </div>
                    }

                    <div className="product-action">
                        {/* eslint-disable-next-line*/}
                        <a href="#" className={`btn btn-dark add-cart shopping-cart mr-2`} title={t("label.buy")}
                           onClick={onAddCartClick}>{t("label.buy")}</a>
                    </div>

                    {showTabs && <ProductTabs product={product}/>}
                </div>
            }
        </>
    )
}

const mapStateToProps = (state) => {
    return {
        user: state.auth.user,
    }
}

const mapDispatchToProps = {
    showLoginModal,
    addProductToCart,
    saveCart,
}

export default connect(mapStateToProps, mapDispatchToProps)(ProductDetails);
